import { inject, Injectable } from "@angular/core";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductService } from "@domain/product/product.service";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { MonitoringDirectBomEntry } from "@domain/project/direct-bom/monitoring-direct-bom-entry";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { MonitoringConfigDataService } from "@domain/project/monitoring-product-data/monitoring-config-data.service";
import { MonitoringProductData } from "@domain/project/monitoring-product-data/monitoring-product-data";
import { MonitoringConfigProductMap } from "@domain/project/monitoring-product-list/monitoring-config-product-map";
import { MonitoringProject as Project } from "@domain/project/monitoring-project";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { forkJoin, map, Observable, of } from "rxjs";

type MonitoringConfig = ProductConfiguration & { getAllItems(): MonitoringProductItem[] };

@Injectable({ providedIn: "root" })
export class MonitoringProductDataService {
  private monitoringProductService = inject(MonitoringProductService);
  private productService = inject(ProductService);
  private dataService = inject(MonitoringConfigDataService);

  collectGroupData(
    group: ProductItemGroup,
    floorplan: Floorplan,
  ): Observable<
    [
      MonitoringConfigProductMap[],
      MonitoringConfigProductMap[],
      MonitoringConfigProductMap[],
      MonitoringConfigProductMap[],
      MonitoringConfigProductMap[],
      MonitoringConfigProductMap[],
    ]
  > {
    const patientMonitorConfigurations: PatientMonitorConfiguration[] = group.references
      .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.PATIENT_MONITORS)
      .map((r) => floorplan.patientMonitors.find((pm) => pm.id === r.floorplanProductItemId))
      .filter((pm): pm is FloorplanPatientMonitor => !!pm)
      .map((pm) => pm.config);
    const centralStationConfigurations: CentralStationConfiguration[] = group.references
      .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.CENTRAL_STATIONS)
      .map((r) => floorplan.centralStations.find((cs) => cs.id === r.floorplanProductItemId))
      .filter((cs): cs is FloorplanCentralStation => !!cs)
      .map((cs) => cs.config);
    const telemetryConfigurations: TelemetryConfiguration[] = group.references
      .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.TELEMETRY)
      .map((r) => floorplan.telemetries.find((t) => t.id === r.floorplanProductItemId))
      .filter((t): t is FloorplanTelemetry => !!t)
      .map((t) => t.config);
    const kvmConfigurations: KvmConfiguration[] = group.references
      .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.KVMS)
      .map((r) => floorplan.kvms.find((kvm) => kvm.id === r.floorplanProductItemId))
      .filter((kvm): kvm is FloorplanKvm => !!kvm)
      .map((kvm) => kvm.config);
    const displayConfigurations: DisplayConfiguration[] = group.references
      .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.DISPLAYS)
      .map((r) => floorplan.displays.find((d) => d.id === r.floorplanProductItemId))
      .filter((d): d is FloorplanDisplay => !!d)
      .map((d) => d.config);
    const utilityConfigurations: UtilityConfiguration[] = group.references
      .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.UTILITY)
      .map((r) => floorplan.utilities.find((u) => u.id === r.floorplanProductItemId))
      .filter((u): u is FloorplanUtility => !!u)
      .map((u) => u.config);

    return forkJoin([
      this.assign(patientMonitorConfigurations, MonitoringCategory.PATIENT_MONITORS),
      this.assign(centralStationConfigurations, MonitoringCategory.CENTRAL_STATIONS),
      this.assign(telemetryConfigurations, MonitoringCategory.TELEMETRY),
      this.assign(kvmConfigurations, MonitoringCategory.KVMS),
      this.assign(displayConfigurations, MonitoringCategory.DISPLAYS),
      this.assign(utilityConfigurations, MonitoringCategory.UTILITY),
    ]);
  }

  collectAdditionalConfigData(
    project: Project,
  ): Observable<
    [
      MonitoringConfigProductMap[],
      MonitoringConfigProductMap[],
      MonitoringConfigProductMap[],
      MonitoringConfigProductMap[],
      MonitoringConfigProductMap[],
      MonitoringConfigProductMap[],
    ]
  > {
    const withAdditional = <T extends ProductConfiguration>(configs: readonly T[]) =>
      configs.filter((c) => c.additionalProductListQuantity > 0) as (T & MonitoringConfig)[];

    return forkJoin([
      this.dataService.assignProductToConfiguration(
        [],
        withAdditional(project.patientMonitors),
        MonitoringCategory.PATIENT_MONITORS,
      ),
      this.dataService.assignProductToConfiguration(
        [],
        withAdditional(project.centralStations),
        MonitoringCategory.CENTRAL_STATIONS,
      ),
      this.dataService.assignProductToConfiguration([], withAdditional(project.telemetries), MonitoringCategory.TELEMETRY),
      this.dataService.assignProductToConfiguration([], withAdditional(project.kvms), MonitoringCategory.KVMS),
      this.dataService.assignProductToConfiguration([], withAdditional(project.displays), MonitoringCategory.DISPLAYS),
      this.dataService.assignProductToConfiguration([], withAdditional(project.utilities), MonitoringCategory.UTILITY),
    ]);
  }

  collectDirectBomData(entries: readonly MonitoringDirectBomEntry[]): Observable<Map<string, MonitoringProductData[]>> {
    if (entries.length === 0) {
      return of(new Map<string, MonitoringProductData[]>());
    }

    return forkJoin(
      entries.map((entry) =>
        this.monitoringProductService
          .loadDevicesFromDB(
            entry.lines.map((line) => line.partNumber),
            entry.category,
            this.productService.getFilterCountry(),
          )
          .pipe(
            map((devices) => {
              const byMatNr = new Map(devices.map((device) => [device.matNr, device] as const));
              return [
                entry.id,
                entry.lines.map((line) => {
                  const device = byMatNr.get(line.partNumber);
                  return device
                    ? MonitoringProductData.create(device, line.quantity)
                    : MonitoringProductData.createFallback(line.partNumber, line.name, line.quantity);
                }),
              ] as const;
            }),
          ),
      ),
    ).pipe(map((mappedEntries) => new Map(mappedEntries)));
  }

  private assign<T extends MonitoringConfig>(configs: T[], category: MonitoringCategory) {
    const unique = Array.from(new Map(configs.map((c) => [c.id, c])).values()) as T[];
    return this.dataService.assignProductToConfiguration(configs, unique, category);
  }
}
